/**
 * StandaloneDraftBoard - Read-only full-viewport auction draft board.
 * Opens in a separate tab via "DRAFT BOARD ↗" from the draft room.
 * Connected via WebSocket; never clears once first state is received.
 */
import React, { useRef, useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Wifi, WifiOff, Sun, Moon, DollarSign } from "lucide-react";
import confetti from "canvas-confetti";
import { useDraft } from "../../contexts/DraftContext";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth, authHeaders } from "../../contexts/AuthContext";
import LegalDisclaimer from "../LegalDisclaimer";
import ManagerName from "../ManagerName";
import { PlayerHeadshot } from "../PlayerHeadshot";
import FlipCard from "../FlipCard";
import TabErrorBoundary from "../analytics/TabErrorBoundary";
import BoardStyleSelector from "../BoardStyleSelector";
import { useBoardStyle } from "../../hooks/useBoardStyle";
import { BOARD_STYLES } from "../../utils/boardStyles";
import TraditionalDraftBoard from "./boards/TraditionalDraftBoard";
import WhiteboardDraftBoard from "./boards/WhiteboardDraftBoard";
import TeamTile from "./boards/TeamTile";
import PlayerCell, { PICK_CELL_MIN_H } from "./boards/PlayerCell";
import DraftBoardChatTicker from "./DraftBoardChatTicker";
import DraftEndRecapModal from "./DraftEndRecapModal";
import FullscreenToggle from "./FullscreenToggle";
import PlayerAnalysisModal from "./PlayerAnalysisModal";

const POSITION_COLORS = {
  QB: "#E03030", RB: "#00C853", WR: "#007AFF",
  TE: "#C97000", K: "#B0B0B0",  DEF:"#B0B0B0",
};

const API = process.env.REACT_APP_BACKEND_URL;

// [Founder Follow-up 1 - Feb 2026] Max-bid restore. Reserves $1 per
// remaining roster spot other than the current pick so a manager can
// never bid themselves into an unfillable roster. Same formula as the
// pre-TD-100 tile (retired during the board-sweep extraction, restored
// here verbatim on explicit founder request).
function calcMaxBid(manager, rosterSize) {
  const spotsLeft = rosterSize - (manager?.roster?.length ?? 0);
  return Math.max(1, (manager?.budget ?? 0) - Math.max(0, spotsLeft - 1));
}

// ── Bid fireworks style registry ───────────────────────────────────────────
// Founder is A/B testing the visual treatment for the $ amount that fires
// onto the leading manager's tile during bidding. Each entry defines a
// label, an inline-style generator (positionColor → React style object),
// and optional extra className(s) (for keyframe-driven variants).
const BID_STYLES = {
  solid: {
    label: "Solid Color",
    style: (c) => ({ color: c }),
  },
  solid_glow: {
    label: "Solid + Glow",
    style: (c) => ({
      color: c,
      textShadow: `0 0 16px ${c}, 0 0 32px ${c}aa, 0 0 56px ${c}66`,
    }),
  },
  solid_shadow: {
    label: "Solid + Depth Shadow",
    style: (c) => ({
      color: c,
      textShadow: "2px 4px 0 rgba(0,0,0,0.55), 4px 8px 14px rgba(0,0,0,0.45)",
    }),
  },
  white_glow: {
    label: "White + Color Glow",
    style: (c) => ({
      color: "#FFFFFF",
      textShadow: `0 0 12px ${c}, 0 0 24px ${c}, 0 0 40px ${c}80`,
    }),
  },
  metallic: {
    label: "Metallic Sheen",
    className: "animate-bid-shimmer",
    style: (c) => ({
      backgroundImage: `linear-gradient(105deg, ${c} 0%, ${c} 35%, #FFFFFF 50%, ${c} 65%, ${c} 100%)`,
      backgroundSize: "300% 100%",
      WebkitBackgroundClip: "text",
      backgroundClip: "text",
      color: "transparent",
      WebkitTextFillColor: "transparent",
      filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.45))",
    }),
  },
  bold_black: {
    label: "Bold Black (Day)",
    style: () => ({
      color: "#000000",
      WebkitTextStroke: "1px rgba(0,0,0,0.7)",
    }),
  },
  bold_white: {
    label: "Bold White (Night)",
    style: () => ({
      color: "#FFFFFF",
      WebkitTextStroke: "1px rgba(255,255,255,0.65)",
    }),
  },
  three_d: {
    label: "3D Numbers",
    style: (c) => ({
      color: c,
      // Stacked dark shadows to extrude the glyph downward-right
      textShadow: [
        "1px 1px 0 rgba(0,0,0,0.85)",
        "2px 2px 0 rgba(0,0,0,0.80)",
        "3px 3px 0 rgba(0,0,0,0.75)",
        "4px 4px 0 rgba(0,0,0,0.70)",
        "5px 5px 0 rgba(0,0,0,0.65)",
        "6px 6px 12px rgba(0,0,0,0.55)",
      ].join(", "),
    }),
  },
  solid_pulse: {
    label: "Solid Pulsing",
    className: "animate-bid-pulse",
    style: (c) => ({ color: c }),
  },
};
const BID_STYLE_DEFAULT = "metallic";
const BID_STYLE_STORAGE_KEY = "pgf_bid_style_v1";

function getInitialBidStyle() {
  try {
    const stored = window.localStorage.getItem(BID_STYLE_STORAGE_KEY);
    if (stored && BID_STYLES[stored]) return stored;
  } catch (_) { /* ignore */ }
  return BID_STYLE_DEFAULT;
}

// Module-level: survives component remounts (iOS tab-switch backgrounding)
let _boardCache = null;

// ── Current-on-auction tile (NEW-1) ────────────────────────────────────────
// Wide horizontal tile shown above all manager tiles when state === BIDDING.
// Background colored to player position. Renders ONLY during active auction
// (BIDDING) - hides during NOMINATION/PRE_BIDDING.
function CurrentPlayerOnAuctionTile({ draftState, isDark }) {
  const nomination = draftState?.current_nomination;
  const isBidding = draftState?.state === "BIDDING" ||
                    draftState?.state === "GOING_ONCE" ||
                    draftState?.state === "GOING_TWICE";
  // [HF clocks-on-tile] Hooks must run before any early return so the
  // useTimer subscription doesn't churn on every BIDDING entry.
  const timeLeft = useTimer(draftState?.timer_remaining, draftState?.timer_version);
  if (!isBidding || !nomination?.player) return null;
  const p = nomination.player;
  const posColor = POSITION_COLORS[p.position] || "#777";
  const subColor = isDark ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.85)";
  const chipBg = isDark ? "rgba(0,0,0,0.25)" : "rgba(255,255,255,0.35)";
  // [HF clocks-on-tile] Two synced clock surfaces - flanking the centered
  // name+team block (founder spec v3: clocks live BETWEEN position-chip
  // and name on the left, and BETWEEN name and PPG on the right). Both
  // consume the same `useTimer` subscription above so they're frame-
  // identical. Font bumped to 40px (was 28px) per founder feedback -
  // "big enough to read across a living room" for in-person drafts.
  const tMins = String(Math.floor(Math.max(0, timeLeft) / 60)).padStart(2, "0");
  const tSecs = String(Math.max(0, timeLeft) % 60).padStart(2, "0");
  const isLowTime = timeLeft <= 5 && timeLeft > 0;
  const clockColor = isLowTime ? "#FFFFFF" : "rgba(255,255,255,0.95)";
  const clockSubColor = "rgba(255,255,255,0.65)";
  const clockBg = isLowTime ? "rgba(0,0,0,0.45)" : "rgba(0,0,0,0.20)";
  const clockBorder = isLowTime ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.20)";
  const renderClock = (side) => (
    <div
      className={`flex flex-col items-center justify-center rounded-lg shrink-0 px-4 py-2 ${isLowTime ? "animate-pulse" : ""}`}
      style={{
        background: clockBg,
        border: `1px solid ${clockBorder}`,
        minWidth: 120,
      }}
      data-testid={`tile-clock-${side}`}
    >
      <p
        className="font-mono font-black tabular-nums leading-none"
        style={{ color: clockColor, fontSize: 40 }}
        data-testid={`tile-clock-${side}-value`}
      >
        {tMins}:{tSecs}
      </p>
      <p
        className="font-display font-bold uppercase tracking-widest mt-1"
        style={{ color: clockSubColor, fontSize: 9 }}
      >
        Remaining
      </p>
    </div>
  );
  return (
    <div
      data-testid="current-player-on-auction-tile"
      className="rounded-xl border p-3 mb-2 flex items-center gap-3 overflow-hidden"
      style={{
        background: `linear-gradient(90deg, ${posColor} 0%, ${posColor}cc 100%)`,
        borderColor: `${posColor}`,
        minHeight: 56,
      }}
    >
      {/* LEFT clock */}
      {renderClock("left")}

      {/* CENTER - photo + position chip + name + team · bye */}
      <div className="flex-1 min-w-0 flex items-center justify-center gap-3">
        <div className="shrink-0" data-testid="tile-player-photo">
          <PlayerHeadshot
            playerId={nomination.player?.id}
            position={p.position}
            size="sm"
          />
        </div>
        <div className="min-w-0 text-center">
          <div className="flex items-center justify-center gap-2 mb-0.5">
            <span
              className="font-display font-black text-xs uppercase tracking-wider px-1.5 py-0.5 rounded"
              style={{ background: chipBg, color: "#FFFFFF" }}
              data-testid="tile-position-chip"
            >
              {p.position}
            </span>
            <p className="font-display font-black text-lg sm:text-xl uppercase leading-tight text-white truncate">
              {p.name}
            </p>
          </div>
          <p
            className="font-body text-xs truncate"
            style={{ color: subColor }}
            data-testid="tile-team-bye"
          >
            {p.nfl_team || "FA"}
            {p.bye_week != null ? ` · Bye ${p.bye_week}` : ""}
          </p>
        </div>
      </div>

      {/* RIGHT clock */}
      {renderClock("right")}

      {/* PPG */}
      <div className="text-right shrink-0">
        <p className="font-display font-black text-2xl text-white leading-none">
          {p.projected_points != null
            ? (Number(p.projected_points) / 17).toFixed(1)
            : "-"}
        </p>
        <p className="font-body text-[9px] uppercase tracking-widest text-white/75 mt-0.5">PPG</p>
      </div>
    </div>
  );
}

// ── Bid style picker (popover menu next to Day/Night toggle) ───────────────
function BidStylePicker({ value, onChange, isDark }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Click-outside close
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const iconColor   = isDark ? "#F5F5F5" : "#0A0A0A";
  const menuBg      = isDark ? "#1C1C1C" : "#FFFFFF";
  const menuBorder  = isDark ? "#2E2E2E" : "#D0D0D0";
  const itemText    = isDark ? "#F5F5F5" : "#0A0A0A";
  const itemHover   = isDark ? "#2A2A2A" : "#F0F0F0";
  const itemActive  = "#FF6B00";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        data-testid="bid-style-picker-btn"
        aria-label="Bid style picker"
        onClick={() => setOpen((o) => !o)}
        className="w-5 h-5 flex items-center justify-center rounded transition-colors"
        style={{ opacity: open ? 1 : 0.6 }}
      >
        <DollarSign size={11} style={{ color: iconColor }} />
      </button>
      {open && (
        <div
          data-testid="bid-style-picker-menu"
          className="absolute right-0 top-7 z-50 rounded-lg shadow-2xl py-1 min-w-[180px]"
          style={{ background: menuBg, border: `1px solid ${menuBorder}` }}
        >
          {Object.entries(BID_STYLES).map(([key, entry]) => {
            const active = key === value;
            return (
              <button
                key={key}
                type="button"
                data-testid={`bid-style-option-${key}`}
                onClick={() => { onChange(key); setOpen(false); }}
                className="w-full text-left px-3 py-1.5 font-body text-xs transition-colors flex items-center justify-between gap-3"
                style={{ color: active ? itemActive : itemText }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = itemHover; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <span>{entry.label}</span>
                {active && <span className="font-display font-black text-[10px]">●</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Sale fireworks - full-board confetti layer, driven by celebrationPhase ─
const POSITION_COLORS_FIREWORKS = {
  QB: "#E03030", RB: "#00C853", WR: "#007AFF",
  TE: "#C97000", K: "#B0B0B0",  DEF: "#B0B0B0",
};

function SaleFireworks({ celebrationPhase }) {
  const [opacity, setOpacity] = useState(0);
  const firedForRef = useRef(null);

  useEffect(() => {
    if (!celebrationPhase) { setOpacity(0); return; }
    const { sale, expiresAt } = celebrationPhase;
    const key = sale?.nomination_id;
    if (firedForRef.current === key) return;
    firedForRef.current = key;

    const posColor = POSITION_COLORS_FIREWORKS[sale?.player?.position] || "#FF6B00";
    setOpacity(1);

    confetti({
      particleCount: 140,
      spread: 85,
      origin: { y: 0.45 },
      colors: [posColor, "#FFD700", "#C0C0C0", posColor],
      ticks: 200,
      gravity: 0.9,
      scalar: 1.1,
    });

    // Fade out in the final 500ms of the celebration window
    const msRemaining = expiresAt - Date.now();
    const fadeDelay = Math.max(0, msRemaining - 500);
    const fadeTimer = setTimeout(() => setOpacity(0), fadeDelay);
    return () => clearTimeout(fadeTimer);
  }, [celebrationPhase]);

  if (!celebrationPhase) return null;

  return (
    <div
      className="pointer-events-none fixed inset-0 z-50 transition-opacity duration-500"
      style={{ opacity }}
    />
  );
}

// ── Timer hook (mirrors ActiveNomination - no shared utility to avoid bloat) ─
function useTimer(timerRemaining, timerVersion) {
  const [timeLeft, setTimeLeft] = useState(timerRemaining || 0);
  const ref = useRef(null);
  const trRef = useRef(timerRemaining);
  trRef.current = timerRemaining;
  useEffect(() => {
    const remaining = trRef.current || 0;
    setTimeLeft(remaining);
    clearInterval(ref.current);
    if (remaining > 0) {
      ref.current = setInterval(() => setTimeLeft(p => Math.max(0, p - 1)), 1000);
    }
    return () => clearInterval(ref.current);
  }, [timerVersion]);
  return timeLeft;
}

// ── Nomination modal - overlays the board during NOMINATION and PRE_BIDDING ─
function NominationModal({ draftState, celebrationPhase, isDark }) {
  const engineState = draftState?.state;
  const nomination  = draftState?.current_nomination;
  const nominator   = draftState?.managers?.[draftState?.current_nominator_id];
  const timeLeft    = useTimer(draftState?.timer_remaining, draftState?.timer_version);
  const isPreBidding = engineState === "PRE_BIDDING";

  // Visual countdown for get-ready (cosmetic - dismissal is server-driven via isPreBidding)
  const [getReadyCount, setGetReadyCount] = useState(10);
  const getReadyRef = useRef(null);
  useEffect(() => {
    if (isPreBidding) {
      setGetReadyCount(10);
      clearInterval(getReadyRef.current);
      getReadyRef.current = setInterval(() => {
        setGetReadyCount(n => Math.max(0, n - 1));
      }, 1000);
    } else {
      clearInterval(getReadyRef.current);
    }
    return () => clearInterval(getReadyRef.current);
  }, [isPreBidding]);

  // Don't show during celebration window (confetti is running)
  if (celebrationPhase) return null;

  // Theme-aware tokens
  const overlayBg  = isDark ? "rgba(0,0,0,0.75)" : "rgba(240,240,240,0.85)";
  const overlayBg2 = isDark ? "rgba(0,0,0,0.80)" : "rgba(240,240,240,0.88)";
  const cardBg     = isDark ? "rgba(17,17,17,0.96)" : "rgba(255,255,255,0.98)";
  const titleColor = isDark ? "#F5F5F5" : "#0A0A0A";
  const subColor   = isDark ? "rgba(255,255,255,0.50)" : "rgba(0,0,0,0.50)";
  const labelColor = isDark ? "rgba(255,255,255,0.40)" : "rgba(0,0,0,0.45)";

  // ── ON THE CLOCK - NOMINATION state with no active nomination ─────────────
  if (engineState === "NOMINATION" && !nomination) {
    const mins = String(Math.floor(timeLeft / 60)).padStart(2, "0");
    const secs = String(timeLeft % 60).padStart(2, "0");
    const timerColor = timeLeft <= 10 ? "#FF6B00" : titleColor;
    return (
      <motion.div
        key="on-the-clock"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="absolute inset-0 z-40 flex items-center justify-center"
        style={{ backdropFilter: "blur(4px)", background: overlayBg }}
      >
        <div
          className="text-center px-10 py-8 rounded-2xl border border-[#FF6B00]/30"
          style={{ background: cardBg, boxShadow: "0 0 60px rgba(255,107,0,0.15)" }}
        >
          <p className="font-display font-black text-xs uppercase tracking-[0.4em] text-[#FF6B00] mb-3">
            On the Clock
          </p>
          <p className="font-display font-black text-4xl uppercase mb-6 leading-tight" style={{ color: titleColor }}>
            <ManagerName name={nominator?.team_name || "-"} isBot={!!nominator?.is_bot} iconSize={20} testId="board-on-clock" />
          </p>
          <p className="font-mono font-black text-5xl" style={{ color: timerColor }}>
            {mins}:{secs}
          </p>
        </div>
      </motion.div>
    );
  }

  // ── GET READY - PRE_BIDDING state ─────────────────────────────────────────
  if (isPreBidding && nomination) {
    const p = nomination.player;
    const posColor = POSITION_COLORS[p?.position] || "#FF6B00";
    const nominatingTeam = draftState?.managers?.[nomination.nominated_by];
    return (
      <motion.div
        key="get-ready"
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        transition={{ duration: 0.2 }}
        className="absolute inset-0 z-40 flex items-center justify-center"
        style={{ backdropFilter: "blur(4px)", background: overlayBg2 }}
      >
        <div
          className="text-center px-10 py-8 rounded-2xl border"
          style={{
            background: cardBg,
            borderColor: `${posColor}40`,
            boxShadow: `0 0 60px ${posColor}20`,
          }}
        >
          <p className="font-display font-black text-xs uppercase tracking-[0.4em] mb-3" style={{ color: posColor }}>
            <ManagerName name={nominatingTeam?.team_name || "A team"} isBot={!!nominatingTeam?.is_bot} iconSize={11} testId="board-nominator" /> nominates
          </p>
          <p className="font-display font-black text-5xl uppercase leading-tight mb-1" style={{ color: titleColor }}>
            {p?.name}
          </p>
          <p className="font-body text-sm mb-4" style={{ color: subColor }}>
            {p?.nfl_team} · Bye {p?.bye_week}
          </p>
          <div
            className="inline-block px-4 py-1 rounded-full mb-6 font-display font-bold text-sm uppercase tracking-wider"
            style={{ background: `${posColor}20`, color: posColor, border: `1px solid ${posColor}40` }}
          >
            Starting bid: ${nomination.current_bid}
          </div>
          <p className="font-display font-black text-xs uppercase tracking-[0.3em] mb-2" style={{ color: labelColor }}>
            Get ready to bid
          </p>
          <p className="font-mono font-black text-4xl text-[#FF6B00]">
            {getReadyCount}
          </p>
        </div>
      </motion.div>
    );
  }

  return null;
}


// ── Main component ─────────────────────────────────────────────────────────
export default function StandaloneDraftBoard({ leagueId, league }) {
  const {
    draftState, isConnected, celebrationPhase,
    recapDismissed, dismissRecap,
    leagueDefaultBoardStyle, leagueCommissionerId,
  } = useDraft();
  const { isDark, toggleTheme }     = useTheme();
  const { user } = useAuth();
  const [bidStyle, setBidStyle]     = useState(getInitialBidStyle);
  // [RL-51] PlayerAnalysisModal active player. Set on board-tile click.
  const [analysisPlayer, setAnalysisPlayer] = useState(null);

  // [PGF Draft Board Refresh v1.1 §F.4] Resolve which board style renders.
  const {
    style: boardStyle,
    userOverride: boardUserOverride,
    setLocalStyle: setLocalBoardStyle,
  } = useBoardStyle(leagueId, leagueDefaultBoardStyle);
  const isCommissioner =
    !!user?.id && (user.id === leagueCommissionerId || user.role === "admin");

  // Persist bid-style choice across sessions
  useEffect(() => {
    try { window.localStorage.setItem(BID_STYLE_STORAGE_KEY, bidStyle); } catch (_) {}
  }, [bidStyle]);

  // Use module-level cache - persists across iOS tab-switch remounts
  if (draftState) {
    _boardCache = draftState;
  }
  const state = _boardCache;

  // Dedup + order by nomination_order
  const rawManagers = state?.managers ? Object.values(state.managers) : [];
  const uniqueManagers = Object.values(
    rawManagers.reduce((acc, m) => { acc[m.user_id] = m; return acc; }, {})
  );
  const nomOrder = state?.nomination_order || [];
  const orderedManagers = nomOrder.length
    ? nomOrder.map(uid => uniqueManagers.find(m => m.user_id === uid)).filter(Boolean)
    : uniqueManagers;

  const rosterSize = state?.roster_size || state?.settings?.roster_size || 13;
  const rounds     = Array.from({ length: rosterSize }, (_, i) => i + 1);
  const getCell    = (uid, idx) => state?.managers?.[uid]?.roster?.[idx] || null;

  // [PGF Draft Board Sweep Items 1+2] Reliable round-beat source. The WS
  // `completed_sales` broadcast is trimmed to the last 20 for bandwidth
  // (DraftContext.js L38), so `completed_sales.length` alone breaks after
  // pick 20 (every round past round 2). We bootstrap a de-duplicated set
  // of sale keys from the untrimmed GET /full-history endpoint once on
  // mount, then grow it incrementally from WS updates keyed by
  // nomination_id - giving a true, unbounded total-sales count with zero
  // extra polling and no backend changes.
  const seenSaleKeysRef = useRef(new Set());
  const [totalSalesCount, setTotalSalesCount] = useState(0);
  const fetchedHistoryLeagueIdRef = useRef(null);
  useEffect(() => {
    if (!leagueId || fetchedHistoryLeagueIdRef.current === leagueId) return;
    fetchedHistoryLeagueIdRef.current = leagueId;
    axios.get(`${API}/api/draft/${leagueId}/full-history`, { headers: authHeaders() })
      .then((r) => {
        const hist = r?.data?.history || [];
        hist.forEach((h) => seenSaleKeysRef.current.add(String(h.request_id || h.overall_pick)));
        setTotalSalesCount(seenSaleKeysRef.current.size);
      })
      .catch(() => { /* falls back to WS-streamed completed_sales only */ });
  }, [leagueId]);
  useEffect(() => {
    const streamed = draftState?.completed_sales;
    if (!streamed?.length) return;
    let changed = false;
    streamed.forEach((s) => {
      const key = String(s.nomination_id || s.sale_id || `${s.player?.id}-${s.sold_at}`);
      if (!seenSaleKeysRef.current.has(key)) {
        seenSaleKeysRef.current.add(key);
        changed = true;
      }
    });
    if (changed) setTotalSalesCount(seenSaleKeysRef.current.size);
  }, [draftState?.completed_sales]);

  // [Undo item 5] The set above only ever GREW, so after a commissioner undo
  // the total stayed high and every subsequent round beat fired one pick
  // early for the rest of the draft. This surface only sees state (extras
  // like the undone sale ride on `msg.extra`), so rebuild the untrimmed set
  // from the authoritative history endpoint whenever undo_count rises.
  const lastUndoCountRef = useRef(draftState?.undo_count ?? 0);
  useEffect(() => {
    const undoCount = draftState?.undo_count ?? 0;
    if (undoCount <= lastUndoCountRef.current) {
      lastUndoCountRef.current = undoCount;
      return;
    }
    lastUndoCountRef.current = undoCount;
    if (!leagueId) return;
    axios.get(`${API}/api/draft/${leagueId}/full-history`, { headers: authHeaders() })
      .then((r) => {
        const hist = r?.data?.history || [];
        seenSaleKeysRef.current = new Set(
          hist.map((h) => String(h.request_id || h.overall_pick)),
        );
        setTotalSalesCount(seenSaleKeysRef.current.size);
      })
      .catch(() => { /* leave the count as-is rather than guess */ });
  }, [draftState?.undo_count, leagueId]);

  // Round beat - fires once per round boundary crossing (roster-slot row
  // index, matching DraftContext.js NOMINATION_READY's own semantics).
  const orderLen = orderedManagers.length || 1;
  const [roundBeat, setRoundBeat] = useState(null);
  const lastRoundBeatCountRef = useRef(0);
  useEffect(() => {
    if (
      totalSalesCount > 0 &&
      totalSalesCount % orderLen === 0 &&
      totalSalesCount !== lastRoundBeatCountRef.current
    ) {
      lastRoundBeatCountRef.current = totalSalesCount;
      setRoundBeat({ round: totalSalesCount / orderLen + 1, key: totalSalesCount });
    }
  }, [totalSalesCount, orderLen]);

  // Auto-scroll the current round's row into view on the round beat.
  const roundRowRefs = useRef({});
  useEffect(() => {
    if (!roundBeat) return;
    const el = roundRowRefs.current[roundBeat.round];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [roundBeat]);

  // Live bid data - always from real-time draftState
  const nomination  = draftState?.current_nomination;
  const posColor    = POSITION_COLORS[nomination?.player?.position] || null;
  const curBid      = nomination?.current_bid || null;
  const curBidderId = nomination?.current_bidder_id || null;
  const bidStyleEntry = BID_STYLES[bidStyle] || BID_STYLES[BID_STYLE_DEFAULT];
  const bidSpanStyle = {
    fontSize: "clamp(1.4rem, 2.8vw, 2.4rem)",
    ...bidStyleEntry.style(posColor),
  };
  const bidClassName = bidStyleEntry.className || "";

  // [NEW-2] Previous high bidder tracking - captures (bidder, amount) right
  // before a higher bid lands, then fades to 0 over ~1s starting at t+1s
  // (total ~2s visible window). Reset on nomination change or sale.
  const prevBidRef = useRef({ bidderId: null, amount: null });
  const [prevBidder, setPrevBidder] = useState({ bidderId: null, amount: null });
  const [prevFading, setPrevFading] = useState(false);
  const fadeTimerRef = useRef(null);

  useEffect(() => {
    // Reset on nomination switch / sale complete
    if (!curBidderId || !curBid) {
      prevBidRef.current = { bidderId: null, amount: null };
      setPrevBidder({ bidderId: null, amount: null });
      setPrevFading(false);
      if (fadeTimerRef.current) {
        clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = null;
      }
      return;
    }
    const prev = prevBidRef.current;
    // New bid by a different bidder OR new amount → the OLD high bidder is now "previous"
    if (prev.bidderId && (prev.bidderId !== curBidderId || prev.amount !== curBid)) {
      setPrevBidder({ bidderId: prev.bidderId, amount: prev.amount });
      setPrevFading(false);
      if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current);
      // Stay solid for ~1s, then animate opacity to 0 over CSS 1s duration → total ~2s visible
      fadeTimerRef.current = setTimeout(() => setPrevFading(true), 1000);
    }
    prevBidRef.current = { bidderId: curBidderId, amount: curBid };
  }, [curBidderId, curBid]);

  useEffect(() => () => { if (fadeTimerRef.current) clearTimeout(fadeTimerRef.current); }, []);

  // Theme tokens
  const pageBg      = isDark ? "#111111" : "#F0F0F0";
  const barBg       = isDark ? "#1C1C1C" : "#FFFFFF";
  const barBorder   = isDark ? "#252525" : "#D8D8D8";
  const emptyBg     = isDark ? "#1C1C1C" : "#E8E8E8";
  const emptyBorder = isDark ? "#252525" : "#D0D0D0";
  const rowNumColor = isDark ? "rgba(255,255,255,0.25)" : "rgba(0,0,0,0.30)";
  const leagueColor = isDark ? "rgba(255,255,255,0.50)" : "rgba(0,0,0,0.50)";
  const connColor   = isDark ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.40)";
  const legalBorder = isDark ? "#1C1C1C" : "#D0D0D0";

  return (
    <div className="h-screen flex flex-col relative overflow-hidden" style={{ background: pageBg }}>
      <SaleFireworks celebrationPhase={celebrationPhase} />

      <AnimatePresence>
        <NominationModal
          key={draftState?.current_nomination?.id || "no-nomination"}
          draftState={draftState}
          celebrationPhase={celebrationPhase}
          isDark={isDark}
        />
      </AnimatePresence>

      {/* ── Slim top bar ────────────────────────────────────────────────── */}
      <div
        className="flex items-center px-4 shrink-0"
        style={{ height: 32, borderBottom: `1px solid ${barBorder}`, background: barBg }}
      >
        <div className="w-7 h-5 bg-[#FF6B00] flex items-center justify-center rounded-sm mr-2">
          <span className="font-display font-black text-white text-[9px] uppercase tracking-wider">PGF</span>
        </div>
        <span className="font-display font-bold text-xs uppercase tracking-widest flex-1 truncate" style={{ color: leagueColor }}>
          {league?.name || "Draft Board"}
        </span>
        <div className="flex items-center gap-2">
          {/* [A.2.6] Fullscreen toggle for projector / second-monitor setup */}
          <FullscreenToggle
            className="w-5 h-5 flex items-center justify-center rounded transition-colors"
            color={isDark ? "#F5F5F5" : "#0A0A0A"}
            testId="fullscreen-toggle-auction"
          />
          {/* Day/Night toggle */}
          <button onClick={toggleTheme} data-testid="theme-toggle-btn" className="w-5 h-5 flex items-center justify-center rounded transition-colors" style={{ opacity: 0.6 }}>
            {isDark ? <Sun size={11} className="text-[#F5F5F5]" /> : <Moon size={11} className="text-[#0A0A0A]" />}
          </button>
          {/* [PGF Draft Board Refresh v1.1 §F.1] Board style selector - sits
              between day/night toggle and BidStylePicker. */}
          <BoardStyleSelector
            leagueId={leagueId}
            activeStyle={boardStyle}
            userOverride={boardUserOverride}
            isCommissioner={isCommissioner}
            onLocalStyleChange={setLocalBoardStyle}
          />
          {/* Bid-style picker ($ icon) - cycles between bid fireworks variants */}
          <BidStylePicker value={bidStyle} onChange={setBidStyle} isDark={isDark} />
          {isConnected
            ? <Wifi size={12} className="text-[#00C853]" />
            : <WifiOff size={12} className="text-[#FF4444] animate-pulse" />
          }
          <span className="font-body text-[10px] ml-0.5" style={{ color: connColor }}>
            {isConnected ? "Live" : "Reconnecting"}
          </span>
        </div>
      </div>

      {/* [Snake Board Fixes item 1] Pinned chrome. The auction clock lives
          on the current-player band during BIDDING (not on the top bar),
          so that band has to sit OUTSIDE the scroll pane or the clock
          scrolls away exactly like the snake clock did. `empty:hidden`
          collapses the wrapper during NOMINATION / PRE_BIDDING, when the
          band renders null. */}
      <div className="shrink-0 px-3 pt-3 empty:hidden">
        <CurrentPlayerOnAuctionTile draftState={draftState} isDark={isDark} />
      </div>

      {/* ── Round rows - the ONLY scrolling region ──────────────────────── */}
      <div className="flex-1 min-h-0 overflow-auto px-3 pt-3 pb-3" data-testid="board-scroll-region">
        <TabErrorBoundary
          testid="standalone-board-error-boundary"
          fallbackLabel="Draft board unavailable"
          fallbackHint="The board hit a render error. Bidding + audio + activity feed continue in the main draft tab; reload this window to retry."
        >
        {!_boardCache || orderedManagers.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-40 gap-3">
            <div className="w-6 h-6 border-2 border-[#FF6B00] border-t-transparent rounded-full animate-spin" />
            <p className="font-mono text-[9px]" style={{ color: rowNumColor }}>
              {!_boardCache ? "Waiting for first state..." : `State received, managers: ${orderedManagers.length}`}
            </p>
          </div>
        ) : (
          <div
            className="grid gap-1.5"
            style={{
              gridTemplateColumns: `32px repeat(${orderedManagers.length}, minmax(90px, 1fr))`,
              minWidth: orderedManagers.length * 98 + 40,
            }}
          >
            {/* Top-left corner spacer - sticky to anchor alongside TeamTile row.
                [HF-POST-CHRIS Gap 4.A] Occludes scrolling round-number cells
                from column 0 passing underneath. Theme-aware bg per A.3. */}
            <div
              className="sticky top-0 z-20"
              style={{ background: pageBg }}
              aria-hidden="true"
            />

            {/* Team tile headers */}
            {orderedManagers.map((mgr) => (
              <TeamTile
                key={mgr.user_id}
                manager={mgr}
                isDark={isDark}
                showBudget
                maxBid={calcMaxBid(mgr, rosterSize)}
                currentBid={curBid}
                currentBidderId={curBidderId}
                positionColor={posColor}
                bidSpanStyle={bidSpanStyle}
                bidClassName={bidClassName}
                previousBidderId={prevBidder.bidderId}
                previousBid={prevBidder.amount}
                prevFading={prevFading}
              />
            ))}

            {/* Draft grid rows */}
            {boardStyle === BOARD_STYLES.PGF_STANDARD && rounds.map((roundNum, roundIdx) => (
              <React.Fragment key={roundNum}>
                <div
                  ref={(el) => { roundRowRefs.current[roundNum] = el; }}
                  data-testid={`board-round-label-${roundNum}`}
                  className="flex items-center justify-center"
                  style={{ color: rowNumColor, fontSize: 11 }}
                >
                  <span className="font-body font-bold">{roundNum}</span>
                </div>
                {orderedManagers.map((mgr) => {
                  const cell = getCell(mgr.user_id, roundIdx);
                  return (
                    <div
                      key={`${mgr.user_id}-r${roundNum}`}
                      // [HF2-6 v1.1] Cell container is now a pure positioning
                      // wrapper - no background or border. The FlipCard primitive
                      // handles both faces (empty back + sold front) so the
                      // animation reads as a card turning over, not a fill-in.
                      // The parent grid background shows through mid-flip (~90°).
                      style={{
                        // [Board Tile Polish item 1] Shared pick-cell height -
                        // a team tile is exactly as tall as a player card in
                        // BOTH formats, off one constant (was a local 60).
                        minHeight: PICK_CELL_MIN_H,
                        position: "relative",
                      }}
                    >
                      <FlipCard
                        isFlipped={!!cell}
                        duration={1.0}
                        perspective={1000}
                        testId={cell ? `sold-cell-${mgr.user_id}-r${roundNum}` : undefined}
                        back={
                          <div
                            className="rounded-lg w-full h-full"
                            style={{
                              background: emptyBg,
                              border: `1px solid ${emptyBorder}`,
                            }}
                          />
                        }
                        front={cell ? <PlayerCell player={cell.player} paid={cell.paid} isDark={isDark} showPaid onClick={(p) => p?.id && setAnalysisPlayer(p)} /> : <div />}
                      />
                    </div>
                  );
                })}
              </React.Fragment>
            ))}
            {/* [PGF Draft Board Refresh v1.1 CP-2] Traditional + Whiteboard
                placeholders render below the team-tile row (which stays
                shared per §B.5). Full render lands at CP-4 / CP-5. */}
            {boardStyle === BOARD_STYLES.TRADITIONAL && (
              <div style={{ gridColumn: `1 / span ${orderedManagers.length + 1}` }}>
                <TraditionalDraftBoard
                  orderedManagers={orderedManagers}
                  rounds={rounds}
                  getCell={getCell}
                  isDark={isDark}
                />
              </div>
            )}
            {boardStyle === BOARD_STYLES.WHITEBOARD && (
              <div style={{ gridColumn: `1 / span ${orderedManagers.length + 1}` }}>
                <WhiteboardDraftBoard
                  orderedManagers={orderedManagers}
                  rounds={rounds}
                  getCell={getCell}
                  isDark={isDark}
                  leagueId={leagueId}
                />
              </div>
            )}
          </div>
        )}
        </TabErrorBoundary>
      </div>

      {/* [PGF Draft Board Refresh v1.1 §A] Broadcast-style ticker pinned
          to the bottom edge - user chat + system-event fillers. Never
          renders Adam commentary. Owned solely by StandaloneDraftBoard. */}
      <DraftBoardChatTicker roundBeat={roundBeat} />

      {/* ── Legal bar ──────────────────────────────────────────────────── */}
      <div className="shrink-0 py-1.5 px-3" style={{ borderTop: `1px solid ${legalBorder}` }}>
        <LegalDisclaimer />
      </div>

      {/* [HF3 P0-2] DraftEndRecapModal also mounts here so the modal fires
          even when the user is watching the standalone board (the founder's
          observed failure mode on the snake side; auction parity per founder
          directive). dismissRecap persists via localStorage so dismissing
          here keeps it dismissed in the room tab too. */}
      {draftState?.state === "DRAFT_COMPLETE" && !recapDismissed && leagueId && (
        <DraftEndRecapModal
          leagueId={leagueId}
          onDismiss={dismissRecap}
          surface="standalone-board"
        />
      )}

      {/* [HF4.5] Round modal removed from auction. Auction has no round
          concept matching the snake model; Adam's verbal "Round N begins"
          line handles the rollover beat via DraftContext.js NOMINATION_READY. */}

      {/* [RL-51] PlayerAnalysisModal - mounts on board-tile click. */}
      {analysisPlayer && leagueId && (
        <PlayerAnalysisModal
          leagueId={leagueId}
          player={analysisPlayer}
          onClose={() => setAnalysisPlayer(null)}
          isDark={isDark}
          draftEpoch={draftState?.completed_sales?.length || 0}
        />
      )}
    </div>
  );
}
